import { useEffect, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";

import { LANGAddEditForm } from "@/components/records/child/LANGAddEditForm";
import Popup from "@/components/shared/Popup";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { useLeaData } from "@/queries.ts";
import { LANG_TEMPLATE, type LANG } from "@/models";
import { langMutationFunctions } from "@/models/chld";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiTrash2,
} from "react-icons/fi";

export type LANGEditDialogProps = {
  selectedLang: LANG | undefined;
  langDefaults?: Partial<LANG>;
  onClose: () => void;
  viewOnly?: boolean;
  onDeleted?: (record: LANG) => void;
  footerMode?: "default" | "back";
};

export default function LANGEditDialog({
  selectedLang,
  langDefaults,
  onClose,
  viewOnly = false,
  onDeleted,
  footerMode = "default",
}: LANGEditDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    data: LANG;
    dateChanged: boolean;
  } | null>(null);

  const initialValues: LANG =
    selectedLang ?? ({ ...LANG_TEMPLATE, ...(langDefaults ?? {}) } as LANG);

  const addEditForm = useForm<LANG>({ defaultValues: initialValues });

  useEffect(() => {
    if (selectedLang) {
      addEditForm.reset(selectedLang);
    } else {
      addEditForm.reset({ ...LANG_TEMPLATE, ...(langDefaults ?? {}) });
    }
    addEditForm.clearErrors();
  }, [selectedLang, langDefaults, addEditForm]);

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "LANG",
      toastMessage: "Language record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "LANG",
    toastMessage: "Language record submitted for deletion",
    successMessage: "Language record deleted successfully",
    onSuccess: () => {
      if (selectedLang) onDeleted?.(selectedLang);
      onClose();
    },
  });

  const mutateFns = langMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = langMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<LANG> = (x) => {
    if (!selectedLang) {
      handleSubmitStart();
      mutateFns.handleSave(x);
      return;
    }
    const dateChanged =
      (selectedLang.EffectiveStartDate ?? "") !== (x.EffectiveStartDate ?? "");
    setSubmitConfirm({ data: x, dateChanged });
  };

  const handleConfirmSubmit = () => {
    if (!submitConfirm) return;
    const data = submitConfirm.data;
    setSubmitConfirm(null);
    handleSubmitStart();
    mutateFns.handleSave(data);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (selectedLang) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(selectedLang, true);
    }
  };

  const formId = "frmLANG";
  const hasRole = leaContext.hasRole("LANGEdit");
  const canEdit = hasRole && !viewOnly;
  const isBack = footerMode === "back";

  const deleteButton =
    hasRole && selectedLang ? (
      <LoaderButton
        variant="danger"
        type="button"
        onClick={() => setShowDeleteConfirm(true)}
        loading={isDeleting && isDeleteSubmitting}
        loadingText="Deleting..."
        className="d-flex align-items-center"
      >
        <FiTrash2 className="me-1" />
        Delete
      </LoaderButton>
    ) : undefined;

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm && !submitConfirm}
        header={
          selectedLang == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Add Language Record" : "View Language Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Edit Language Record" : "View Language Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          selectedLang ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText={isBack ? undefined : "Cancel"}
        size="lg"
        onClose={onClose}
        onSecondButtonClick={isBack ? undefined : onClose}
        backButtonText={
          isBack ? (
            <div className="d-flex align-items-center gap-2">
              <FiArrowLeft size={16} />
              <span>Back</span>
            </div>
          ) : undefined
        }
        onBackButtonClick={isBack ? onClose : undefined}
        canEdit={canEdit}
        additionalFooterButton={isBack ? deleteButton : undefined}
        leftFooterButton={isBack ? undefined : deleteButton}
      >
        <Form
          id={formId}
          onSubmit={addEditForm.handleSubmit(onSubmit, () =>
            focusFirstInvalidField(`#${formId}`)
          )}
        >
          <LANGAddEditForm
            selectedLang={selectedLang}
            form={addEditForm}
            langDefaults={langDefaults}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          recordKindLabel="Language record"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {submitConfirm && (
        <ConfirmModal
          title={
            submitConfirm.dateChanged
              ? "Confirm Effective Date Change"
              : "Confirm Record Update"
          }
          message={
            submitConfirm.dateChanged
              ? "The changes requested have a different effective date. They will insert a new record with these updates and automatically expire the old record."
              : "The changes requested will overwrite the existing record. If these changes became effective at a later date than when the record was originally input, please input the associated effective date."
          }
          confirmText="Submit"
          onCancel={() => setSubmitConfirm(null)}
          onConfirm={handleConfirmSubmit}
        />
      )}
    </>
  );
}
